import { useState, useCallback } from "react";
import { Image, LayoutGrid, Merge, Palette, Sparkles } from "lucide-react";
import {
    useLuminanceGrid,
    type BlobRegionMode,
    type CellCornerMode,
} from "./LuminanceGridContext";
import { CheckboxField } from "../reusable/CheckboxField";
import { CollapsibleToolGroup } from "../reusable/CollapsibleToolGroup";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { PresetSelector } from "../reusable/PresetSelector";
import { ToolDescriptionPopover } from "../reusable/ToolDescriptionPopover";
import {
    LUMINANCE_PALETTE_PRESETS,
    findMatchingPalettePresetId,
    equalSplitThresholds,
} from "../../utils/luminancePalette";
import { PaletteThresholdBar } from "../reusable/PaletteThresholdBar";
import { ColorPaletteEditor } from "../reusable/ColorPaletteEditor";

export function LuminanceGridControls() {
    const {
        imageName,
        sourceWidth,
        sourceHeight,
        cols,
        rows,
        effectiveCols,
        effectiveRows,
        gridProductCapped,
        noiseAmount,
        noiseScale,
        seed,
        invert,
        paletteEnabled,
        paletteColors,
        paletteThresholds,
        setCols,
        setRows,
        matchRowsToImageAspect,
        setMatchRowsToImageAspect,
        setNoiseAmount,
        setNoiseScale,
        setSeed,
        setInvert,
        setPaletteEnabled,
        setPaletteColors,
        setPaletteThresholds,
        blobOutlineEnabled,
        setBlobOutlineEnabled,
        blobOutlineLayers,
        updateBlobOutlineLayer,
        addBlobOutlineLayer,
        removeBlobOutlineLayer,
        blobQuantizeToPalette,
        setBlobQuantizeToPalette,
        cellCornerMode,
        setCellCornerMode,
        cellCornerSize,
        setCellCornerSize,
        handleImageLoad,
        imageInputRef,
        applyPreset,
        getPresetValues,
        resetParameters,
    } = useLuminanceGrid();

    const applyPalettePreset = useCallback(
        (presetId: string) => {
            const preset = LUMINANCE_PALETTE_PRESETS.find(
                (p) => p.id === presetId,
            );
            if (!preset) return;
            setPaletteColors([...preset.colors]);
            setPaletteThresholds(equalSplitThresholds(preset.colors.length));
        },
        [setPaletteColors, setPaletteThresholds],
    );

    const onPalettePresetChange = useCallback(
        (presetId: string) => {
            if (presetId === "custom") return;
            applyPalettePreset(presetId);
        },
        [applyPalettePreset],
    );

    const onPaletteColorsChange = useCallback(
        (colors: string[]) => {
            if (colors.length !== paletteColors.length) {
                setPaletteThresholds(equalSplitThresholds(colors.length));
            }
            setPaletteColors(colors);
        },
        [
            paletteColors.length,
            paletteColors,
            setPaletteColors,
            setPaletteThresholds,
        ],
    );

    const handlePositiveFloat =
        (setter: (v: number) => void, min = 0, max = 1) =>
        (v: string | number) => {
            const n = typeof v === "number" ? v : Number(v);
            if (!Number.isFinite(n) || n < min || n > max) return;
            setter(n);
        };

    const handlePositiveInt = (setter: (v: number) => void, max = 10_000) => {
        return (v: string | number) => {
            const n = typeof v === "number" ? v : Number(v);
            if (!Number.isFinite(n) || n < 1 || n > max) return;
            setter(Math.round(n));
        };
    };

    return (
        <div className="panel-controls luminance-grid-controls">
            <div className="tool-header">
                <div className="tool-header-title-row">
                    <h2>Luminance Grid</h2>
                    <ToolDescriptionPopover dialogLabel="Luminance Grid tool description">
                        <p>
                            Raster image to SVG grid of square cells. Luminance
                            matches ASCII Art (Rec. 709). Set columns and rows to
                            control sampling density; the image is scaled to
                            cover the grid.
                        </p>
                    </ToolDescriptionPopover>
                </div>
                <div className="tool-header-actions">
                    <button
                        type="button"
                        className="generic-button"
                        onClick={resetParameters}
                    >
                        Reset parameters
                    </button>
                </div>
            </div>
            <div className="path-tool-inputs">
                <PresetSelector
                    toolId="luminance-grid"
                    onApply={applyPreset}
                    onExport={getPresetValues}
                />

                <CollapsibleToolGroup title="Image" icon={Image}>
                    <label className="field-label">Image file</label>
                    <div className="upload-controls">
                        <button
                            type="button"
                            className="generic-button"
                            onClick={() => imageInputRef.current?.click()}
                        >
                            Choose image file...
                        </button>
                        <input
                            ref={imageInputRef}
                            type="file"
                            accept="image/*"
                            className="upload-input"
                            onChange={handleImageLoad}
                        />
                    </div>
                    {imageName && (
                        <p className="upload-info">
                            Loaded: <strong>{imageName}</strong>
                        </p>
                    )}
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Grid" icon={LayoutGrid}>
                    {sourceWidth != null && sourceHeight != null && (
                        <p className="upload-info path-tool-meta">
                            Source image: {sourceWidth}x{sourceHeight}px
                        </p>
                    )}

                    <div className="field-row">
                        <label className="field-label">
                            Columns
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={10000}
                                step={1}
                                value={cols}
                                onChange={handlePositiveInt(setCols)}
                                debounceMs={300}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <CheckboxField
                            inline
                            checked={matchRowsToImageAspect}
                            onChange={setMatchRowsToImageAspect}
                            disabled={
                                sourceWidth == null || sourceHeight == null
                            }
                            label="Match rows to image aspect ratio"
                        />
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Rows
                            {matchRowsToImageAspect ? " (auto)" : ""}
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={10000}
                                step={1}
                                value={rows}
                                onChange={handlePositiveInt(setRows)}
                                debounceMs={300}
                                disabled={matchRowsToImageAspect}
                            />
                        </label>
                    </div>

                    {gridProductCapped && (
                        <p className="upload-info path-tool-meta">
                            Cell budget cap: using{" "}
                            <strong>
                                {effectiveCols}x{effectiveRows}
                            </strong>{" "}
                            (requested {cols}x{rows}).
                        </p>
                    )}

                    <div className="field-row">
                        <label className="field-label">
                            Cell corners
                            <select
                                value={cellCornerMode}
                                onChange={(e) =>
                                    setCellCornerMode(
                                        e.target.value as CellCornerMode,
                                    )
                                }
                            >
                                <option value="square">
                                    Square (rectangles)
                                </option>
                                <option value="bevel">
                                    Beveled (chamfer toward neighbors)
                                </option>
                                <option value="round">
                                    Rounded (arc toward neighbors)
                                </option>
                            </select>
                        </label>
                    </div>

                    {cellCornerMode !== "square" && (
                        <>
                            <div className="field-row">
                                <label className="field-label">
                                    Corner size (cell units)
                                    <DebouncedInput
                                        type="number"
                                        min={0.01}
                                        max={0.45}
                                        step={0.01}
                                        value={cellCornerSize}
                                        onChange={handlePositiveFloat(
                                            setCellCornerSize,
                                            0.01,
                                            0.45,
                                        )}
                                        debounceMs={300}
                                    />
                                </label>
                            </div>
                            <p className="upload-info path-tool-meta">
                                Only corners on the outer edge of each solid
                                color region are chamfered (not between two
                                cells of the same color). Depth uses grid units
                                (1 = one cell).
                            </p>
                        </>
                    )}

                    <div className="field-row">
                        <CheckboxField
                            inline
                            checked={invert}
                            onChange={setInvert}
                            label="Invert luminance"
                        />
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Palette" icon={Palette}>
                    <div className="field-row">
                        <CheckboxField
                            inline
                            checked={paletteEnabled}
                            onChange={setPaletteEnabled}
                            label="Map luminance to palette"
                        />
                    </div>

                    {paletteEnabled && (
                        <>
                            <div className="field-row">
                                <label className="field-label">
                                    Palette preset
                                    <select
                                        value={findMatchingPalettePresetId(
                                            paletteColors,
                                        )}
                                        onChange={(e) =>
                                            onPalettePresetChange(
                                                e.target.value,
                                            )
                                        }
                                    >
                                        {LUMINANCE_PALETTE_PRESETS.map((p) => (
                                            <option key={p.id} value={p.id}>
                                                {p.label}
                                            </option>
                                        ))}
                                        <option value="custom">Custom</option>
                                    </select>
                                </label>
                            </div>
                            <div className="field-row">
                                <label className="field-label">
                                    Colors (dark to light)
                                    <ColorPaletteEditor
                                        colors={paletteColors}
                                        onChange={onPaletteColorsChange}
                                        minColors={1}
                                        description="Order is dark to light along luminance. Adjust boundaries on the bar below."
                                    />
                                </label>
                            </div>
                            <PaletteThresholdBar
                                colors={paletteColors}
                                thresholds={paletteThresholds}
                                onThresholdsChange={setPaletteThresholds}
                            />
                        </>
                    )}
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Merged outlines" icon={Merge}>
                    <div className="field-row">
                        <CheckboxField
                            inline
                            checked={blobOutlineEnabled}
                            onChange={setBlobOutlineEnabled}
                            label="Merged outlines (blob)"
                        />
                    </div>

                    {blobOutlineEnabled && (
                        <>
                            <p className="upload-info path-tool-meta">
                                Each layer draws merged outlines for 4-connected
                                cells (per-layer threshold and dark/light). Add
                                layers for multiple thresholds or styles.
                            </p>
                            {paletteEnabled && paletteColors.length >= 2 && (
                                <div className="field-row">
                                    <label className="field-label">
                                        Blob luminance source
                                        <select
                                            value={
                                                blobQuantizeToPalette
                                                    ? "palette"
                                                    : "raw"
                                            }
                                            onChange={(e) =>
                                                setBlobQuantizeToPalette(
                                                    e.target.value ===
                                                        "palette",
                                                )
                                            }
                                        >
                                            <option value="palette">
                                                Palette bucket (quantized index)
                                            </option>
                                            <option value="raw">
                                                Raw luminance (noise + invert)
                                            </option>
                                        </select>
                                    </label>
                                </div>
                            )}
                            {blobOutlineLayers.map((layer, layerIndex) => (
                                <div
                                    key={layer.id}
                                    style={{
                                        marginTop:
                                            layerIndex > 0 ? "0.65rem" : 0,
                                        paddingTop:
                                            layerIndex > 0 ? "0.65rem" : 0,
                                        borderTop:
                                            layerIndex > 0
                                                ? "1px solid rgba(128, 128, 128, 0.35)"
                                                : undefined,
                                    }}
                                >
                                    <div
                                        className="field-row"
                                        style={{
                                            marginBottom: "0.35rem",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            gap: "0.5rem",
                                        }}
                                    >
                                        <span className="path-tool-meta">
                                            Outline layer {layerIndex + 1}
                                        </span>
                                        {blobOutlineLayers.length > 1 && (
                                            <button
                                                type="button"
                                                className="generic-button"
                                                onClick={() =>
                                                    removeBlobOutlineLayer(
                                                        layer.id,
                                                    )
                                                }
                                            >
                                                Remove
                                            </button>
                                        )}
                                    </div>
                                    <div className="field-row">
                                        <label className="field-label">
                                            Blob region
                                            <select
                                                value={layer.blobRegion}
                                                onChange={(e) =>
                                                    updateBlobOutlineLayer(
                                                        layer.id,
                                                        {
                                                            blobRegion: e.target
                                                                .value as BlobRegionMode,
                                                        },
                                                    )
                                                }
                                            >
                                                <option value="dark">
                                                    Dark (luminance ≤ threshold)
                                                </option>
                                                <option value="light">
                                                    Light (luminance ≥
                                                    threshold)
                                                </option>
                                            </select>
                                        </label>
                                    </div>
                                    <div className="field-row">
                                        <label className="field-label">
                                            Luminance threshold
                                            <DebouncedInput
                                                type="number"
                                                min={0}
                                                max={1}
                                                step={0.05}
                                                value={layer.blobThreshold}
                                                onChange={handlePositiveFloat(
                                                    (n) =>
                                                        updateBlobOutlineLayer(
                                                            layer.id,
                                                            {
                                                                blobThreshold:
                                                                    n,
                                                            },
                                                        ),
                                                    0,
                                                    1,
                                                )}
                                                debounceMs={300}
                                            />
                                        </label>
                                    </div>
                                    <div className="field-row">
                                        <label className="field-label">
                                            Outline offset (viewBox units; 1 =
                                            one cell)
                                            <DebouncedInput
                                                type="number"
                                                min={-2}
                                                max={5}
                                                step={0.02}
                                                value={layer.blobOutlineOffset}
                                                onChange={handlePositiveFloat(
                                                    (n) =>
                                                        updateBlobOutlineLayer(
                                                            layer.id,
                                                            {
                                                                blobOutlineOffset:
                                                                    n,
                                                            },
                                                        ),
                                                    -2,
                                                    5,
                                                )}
                                                debounceMs={300}
                                            />
                                        </label>
                                    </div>
                                    <div className="field-row">
                                        <label className="field-label">
                                            Outline stroke width (SVG units)
                                            <DebouncedInput
                                                type="number"
                                                min={0.01}
                                                max={0.5}
                                                step={0.01}
                                                value={layer.blobStrokeWidth}
                                                onChange={handlePositiveFloat(
                                                    (n) =>
                                                        updateBlobOutlineLayer(
                                                            layer.id,
                                                            {
                                                                blobStrokeWidth:
                                                                    n,
                                                            },
                                                        ),
                                                    0.01,
                                                    0.5,
                                                )}
                                                debounceMs={300}
                                            />
                                        </label>
                                    </div>
                                    <div className="field-row">
                                        <label className="field-label">
                                            Outline stroke color
                                            <DebouncedInput
                                                type="text"
                                                value={layer.blobStrokeColor}
                                                onChange={(v) =>
                                                    updateBlobOutlineLayer(
                                                        layer.id,
                                                        {
                                                            blobStrokeColor:
                                                                String(v),
                                                        },
                                                    )
                                                }
                                                debounceMs={300}
                                            />
                                        </label>
                                    </div>
                                </div>
                            ))}
                            <div className="field-row">
                                <button
                                    type="button"
                                    className="generic-button"
                                    onClick={addBlobOutlineLayer}
                                >
                                    Add outline layer
                                </button>
                            </div>
                            <p className="upload-info path-tool-meta">
                                Positive offset expands the outline outward;
                                negative insets. Same coordinate system as the
                                grid (not screen pixels unless 1 SVG unit = 1
                                px).
                            </p>
                        </>
                    )}
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Noise" icon={Sparkles}>
                    <div className="field-row">
                        <label className="field-label">
                            Noise amount
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={1}
                                step={0.05}
                                value={noiseAmount}
                                onChange={handlePositiveFloat(setNoiseAmount)}
                                debounceMs={300}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Noise scale
                            <DebouncedInput
                                type="number"
                                min={0.001}
                                max={2}
                                step={0.01}
                                value={noiseScale}
                                onChange={handlePositiveFloat(
                                    setNoiseScale,
                                    0.001,
                                    2,
                                )}
                                debounceMs={300}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Noise seed
                            <DebouncedInput
                                type="text"
                                value={seed}
                                onChange={(v) => setSeed(String(v))}
                                debounceMs={300}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>
            </div>
        </div>
    );
}
